/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import Head from 'next/head';
import { useRouter } from 'next/router';
import React from 'react';

import {
  defaultDescription,
  defaultOgDescription,
  defaultTitle,
  jsonLd,
} from './constants';

interface Props {
  title?: string;
  description?: string;
  canonical?: string;
  tags?: JSX.Element[]
}

const isProd = process.env.NODE_ENV === 'production';

export const HtmlHead = (props: Props): JSX.Element => {
  const { title = defaultTitle, description = defaultDescription, tags = [] } = props;
  const router = useRouter();
  const ulrForMainPage = `https://welltory.com/`;
  const canonicalUrl =
    props.canonical || `https://app.welltory.com${router.asPath}`;

  let canonicalMainUrl = '';

  if (router.pathname === '/landing/main') canonicalMainUrl = ulrForMainPage;
  else canonicalMainUrl = canonicalUrl;

  const metaTags = [
    <meta name="twitter:title" content="Welltory" />,
    <meta name="twitter:image" content="https://ucarecdn.com/80fd4862-5a93-4105-bfa0-0947517e5924/Welltory.jpg"/>,
    <meta name="twitter:description" content={description || defaultOgDescription}/>,
    <meta name="twitter:card" content="summary_large_image" />,
    <meta key="og:url" property="og:url" content={canonicalUrl} />,
    <meta property="og:title" content="Welltory" />,
    <meta property="og:description" content={description ?? defaultOgDescription} />,
    <meta property="og:image" content="https://ucarecdn.com/80fd4862-5a93-4105-bfa0-0947517e5924/Welltory.jpg" />,
    <meta property="og:image:alt" content="Welltory" />,
    <meta property="og:image:width" content="1200" />,
    <meta property="og:image:height" content="630" />,
    <meta property="og:locale" content="en_US" />,
    ...tags
  ];

  const tagMap = new Map();
  metaTags.forEach(tag => {
    const key = tag.key || `${tag.props.name || tag.props.property}`;
    return tagMap.set(key, tag);
  });
  const uniqueMetaTags = Array.from(tagMap.values());
  
  return (
    <Head>
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover" />
      <link key="canonical" rel="canonical" href={canonicalMainUrl} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta
        name="apple-mobile-web-app-status-bar-style"
        content="black-translucent"
      />
      <meta httpEquiv="msthemecompatible" content="no" />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      {isProd ? (
        <>
          <link rel="icon" type="image/x-icon" href="/favicons/favicon.ico" />
          <link
            rel="icon"
            sizes="192x192"
            href="/favicons/favicon-192x192.png"
          />
          <link rel="apple-touch-icon" href="/favicons/favicon-192x192.png" />
          <link
            rel="apple-touch-icon"
            sizes="180x180"
            href="/favicons/favicon-180x180.png"
          />
          <link
            rel="icon"
            type="image/png"
            sizes="32x32"
            href="/favicons/favicon-32x32.png"
          />
          <link
            rel="icon"
            type="image/png"
            sizes="16x16"
            href="/favicons/favicon-16x16.png"
          />
          <link rel="manifest" href="/favicons/site.webmanifest" />
        </>
      ) : (
        <link rel="icon" sizes="144x144" href="/favicons/favicon-dev.png" />
      )}
      <meta name="format-detection" content="telephone=no" />
      <meta name="msapplication-config" content="/favicons/browserconfig.xml" />
      <meta name="theme-color" content="#ffffff" />

      { uniqueMetaTags }
      
      <meta name="robots" content="index,follow" />
      <meta name="googlebot" content="index,follow" />
    </Head>
  );
};
